import { Link } from "react-router-dom";
import { useState } from "react";
import { Search, Sparkles, ChevronDown, ArrowRight, Layers, MessageCircle } from "lucide-react";
import { Eyebrow, SectionTitle, SectionSub, PrimaryButton } from "@/components/web/atoms";
import { Reveal, BrowserFrame, trackHomeEvent } from "@/components/web/home/primitives";

/* Section 5 — Start AI Research directly. Mirrors the real Ask Composer so a
   visitor understands research starts with a question, not with finding a chat. */
const EXAMPLES = [
  "Compare our top competitors",
  "Research this company before my meeting",
  "Analyze these uploaded files",
  "Compare Claude and ChatGPT",
  "Help me understand this market",
];

function AskComposerMock() {
  const [q, setQ] = useState("");
  return (
    <div className="p-5 sm:p-7 bg-[var(--w-bg)]">
      <div className="text-[13px] font-semibold text-[var(--w-text-dim)] mb-3">What do you want to research?</div>
      <div className="rounded-[14px] border border-[var(--w-hairline)] bg-[var(--w-surface)] p-3">
        <div className="flex items-start gap-2.5">
          <Search className="w-4 h-4 text-[var(--w-text-mute)] mt-2 shrink-0" />
          <textarea
            value={q}
            onChange={(e) => setQ(e.target.value)}
            rows={2}
            placeholder="Ask anything…"
            data-testid="ask-composer-input"
            className="flex-1 bg-transparent resize-none outline-none text-[14px] leading-6 text-[var(--w-text)] placeholder:text-[var(--w-text-mute)]"
          />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[var(--w-hairline)] pt-3">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[var(--w-hairline)] bg-[var(--w-bg2)] text-[12.5px] font-medium text-[var(--w-text-dim)]"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400" /> ChatGPT <ChevronDown className="w-3.5 h-3.5 opacity-60" />
          </button>
          <div className="ml-auto flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[var(--w-surface2)] text-[12.5px] font-semibold text-[var(--w-text)]">
              <MessageCircle className="w-3.5 h-3.5" /> Ask One AI
            </span>
            <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[var(--w-ai)] text-black text-[12.5px] font-semibold">
              <Layers className="w-3.5 h-3.5" /> Compare Models
            </span>
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {EXAMPLES.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => setQ(e)}
            data-testid={`ask-example-${e.slice(0, 8).toLowerCase().replace(/\s+/g, "-")}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--w-hairline)] bg-[var(--w-surface)] text-[12px] text-[var(--w-text-dim)] hover:border-[var(--w-ai)]/40 hover:text-[var(--w-text)] transition-colors"
          >
            <Sparkles className="w-3 h-3 text-[var(--w-ai)]" /> {e}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AskComposerSection() {
  return (
    <section
      id="ask-composer"
      className="relative py-20 md:py-28 px-5 border-t border-[var(--w-hairline)] scroll-mt-20"
      data-testid="home-ask-composer"
    >
      <div className="max-w-6xl mx-auto">
        <Reveal className="max-w-3xl">
          <Eyebrow tone="ai" className="mb-4">Start AI research</Eyebrow>
          <SectionTitle>Research starts with a question — not with finding the right chat.</SectionTitle>
          <SectionSub className="mt-5">
            Type what you want to know, pick one model for speed or compare several when it matters, and
            keep every follow-up connected.
          </SectionSub>
        </Reveal>
        <Reveal delay={120} className="mt-10">
          <BrowserFrame url="teamnest.ai/research">
            <AskComposerMock />
          </BrowserFrame>
        </Reveal>
        <Reveal delay={180} className="mt-8">
          <PrimaryButton
            as={Link}
            to="/ai-research"
            data-testid="ask-composer-cta"
            onClick={() => trackHomeEvent("capability-ai-research", { source: "ask_composer" })}
          >
            Start AI Research <ArrowRight className="w-4 h-4" />
          </PrimaryButton>
        </Reveal>
      </div>
    </section>
  );
}
